/**
 * WidgetStepperModal V2 - Modal simplifié pour ajouter/éditer des widgets
 * 
 * Utilise les nouveaux composants de configuration V2.
 * Architecture simplifiée avec flux de données clair.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { BaseModal } from "@/components/ui/base-modal";
import { Typography, Button } from "@/components/ui";
import {
  FaLayerGroup,
  FaSave,
  FaArrowLeft,
  FaArrowRight,
  FaDatabase,
  FaPalette,
  FaFilter,
  FaCheck,
  FaPaintBrush,
} from "react-icons/fa";
import { FiltersContext } from "@/models/filter";
import {
  WidgetConfig,
  WidgetLayout,
  Widget,
  WidgetType,
  WIDGET_REGISTRY,
  WIDGET_CATEGORIES,
  createDefaultWidgetConfig,
  getWidgetsByCategory,
  widgetRequiresData,
} from "@/models/widget-v2";
import { WidgetDataConfig } from "./WidgetDataConfig";
import { WidgetVisualConfig } from "./WidgetVisualConfig";
import { WidgetFiltersConfig } from "./WidgetFiltersConfig";
import { WidgetAppearanceConfig } from "./WidgetAppearanceConfig";
import { WidgetPreview } from "./WidgetPreview";
import { WidgetThumbnail } from "./WidgetThumbnail";

// ============================================================================
// Types
// ============================================================================

export interface WidgetStepperModalV2Props {
  open: boolean;
  onClose: () => void;
  onAddWidget: (widget: Widget) => void;
  filtersCtx?: FiltersContext;
  editingWidget?: Widget | null;
  onUpdateWidget?: (widget: Widget) => void;
}

// ============================================================================
// Widget Selection Step
// ============================================================================

interface WidgetSelectionStepProps {
  onSelectWidget: (type: WidgetType) => void;
}

const WidgetSelectionStep: React.FC<WidgetSelectionStepProps> = ({ onSelectWidget }) => {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  
  const categories = [
    { key: "all", label: "Tous" },
    { key: WIDGET_CATEGORIES.TEMPORAL, label: "Temporels" },
    { key: WIDGET_CATEGORIES.CATEGORICAL, label: "Catégoriels" },
    { key: WIDGET_CATEGORIES.METRIC, label: "Métriques" },
    { key: WIDGET_CATEGORIES.DATA, label: "Données" },
    { key: WIDGET_CATEGORIES.VISUALIZATION, label: "Visualisation" },
    { key: WIDGET_CATEGORIES.STATIC, label: "Statiques" },
  ];
  
  const filteredWidgets = useMemo(() => {
    if (activeCategory === "all") {
      return Object.values(WIDGET_REGISTRY);
    }
    return getWidgetsByCategory(activeCategory as any);
  }, [activeCategory]);
  
  return (
    <div className="space-y-6">
      {/* Filtres par catégorie */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === cat.key
                ? "bg-accent text-white"
                : "bg-arc-surface-secondary text-content-secondary hover:bg-arc-surface-tertiary"
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>
      
      {/* Grille de widgets */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {filteredWidgets.map((widget) => (
          <div
            key={widget.type}
            role="button"
            tabIndex={0}
            onClick={() => onSelectWidget(widget.type)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectWidget(widget.type);
              }
            }}
            className="flex flex-col items-stretch p-3 rounded-xl border border-arc-border bg-arc-surface hover:bg-arc-surface-secondary hover:border-accent transition-all group cursor-pointer"
          >
            <div className="w-full mb-2 rounded-lg overflow-hidden bg-arc-surface-secondary group-hover:scale-[1.02] transition-transform">
              <WidgetThumbnail type={widget.type} />
            </div>
            <Typography variant="text-sm" className="font-medium text-content-primary text-center">
              {widget.name}
            </Typography>
            <Typography variant="text-xs" className="text-content-tertiary text-center mt-1 line-clamp-2">
              {widget.description}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Widget Configuration Step
// ============================================================================

interface WidgetConfigurationStepProps {
  config: WidgetConfig;
  onConfigChange: (config: WidgetConfig) => void;
  filtersCtx?: FiltersContext;
}

const WidgetConfigurationStep: React.FC<WidgetConfigurationStepProps> = ({
  config,
  onConfigChange,
  filtersCtx,
}) => {
  const [activeTab, setActiveTab] = useState<"data" | "visual" | "appearance" | "filters">("data");
  const requiresData = widgetRequiresData(config.type);
  
  return (
    <div className="flex h-[550px] gap-4">
      {/* Panneau de configuration */}
      <div className="w-1/2 flex flex-col border border-arc-border rounded-lg overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-arc-border bg-arc-surface-secondary">
          <button
            onClick={() => setActiveTab("data")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "data"
                ? "bg-arc-surface text-accent border-b-2 border-accent"
                : "text-content-secondary hover:text-content-primary"
            }`}
            disabled={!requiresData}
          >
            <FaDatabase size={14} />
            Données
          </button>
          <button
            onClick={() => setActiveTab("visual")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "visual"
                ? "bg-arc-surface text-accent border-b-2 border-accent"
                : "text-content-secondary hover:text-content-primary"
            }`}
          >
            <FaPalette size={14} />
            Visuel
          </button>
          <button
            onClick={() => setActiveTab("appearance")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "appearance"
                ? "bg-arc-surface text-accent border-b-2 border-accent"
                : "text-content-secondary hover:text-content-primary"
            }`}
          >
            <FaPaintBrush size={14} />
            Apparence
          </button>
          <button
            onClick={() => setActiveTab("filters")}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "filters"
                ? "bg-arc-surface text-accent border-b-2 border-accent"
                : "text-content-secondary hover:text-content-primary"
            }`}
          >
            <FaFilter size={14} />
            Filtres
          </button>
        </div>
        
        {/* Contenu du tab */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "data" && requiresData && (
            <WidgetDataConfig
              widgetType={config.type}
              config={config.data}
              onChange={(data) => onConfigChange({ ...config, data })}
            />
          )}
          
          {activeTab === "data" && !requiresData && (
            <div className="h-full flex items-center justify-center text-content-tertiary">
              <Typography variant="text-sm">
                Ce widget n'a pas besoin de configuration de données.
              </Typography>
            </div>
          )}
          
          {activeTab === "visual" && (
            <WidgetVisualConfig
              widgetType={config.type}
              config={config.visual}
              onChange={(visual) => onConfigChange({ ...config, visual })}
            />
          )}
          
          {activeTab === "appearance" && (
            <WidgetAppearanceConfig
              config={config.appearance ?? {}}
              onChange={(appearance) => onConfigChange({ ...config, appearance })}
            />
          )}
          
          {activeTab === "filters" && (
            <WidgetFiltersConfig
              independentFilters={config.independentFilters ?? false}
              onIndependentFiltersChange={(checked) => onConfigChange({ ...config, independentFilters: checked })}
              filters={config.filters}
              onFiltersChange={(filters) => onConfigChange({ ...config, filters })}
            />
          )}
        </div>
      </div>
      
      {/* Panneau de prévisualisation */}
      <div className="w-1/2 flex flex-col border border-arc-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-arc-surface-secondary border-b border-arc-border flex items-center justify-between">
          <Typography variant="text-sm" className="font-medium text-content-secondary">
            Aperçu en direct
          </Typography>
          <Typography variant="text-xs" className="text-content-tertiary">
            {WIDGET_REGISTRY[config.type]?.name}
          </Typography>
        </div>
        <div className="flex-1 p-4 bg-arc-surface">
          <WidgetPreview config={config} filtersCtx={filtersCtx} />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const WidgetStepperModalV2: React.FC<WidgetStepperModalV2Props> = ({
  open,
  onClose,
  onAddWidget,
  filtersCtx,
  editingWidget = null,
  onUpdateWidget,
}) => {
  const isEditMode = !!editingWidget;
  
  // État du stepper
  const [activeStep, setActiveStep] = useState(0);
  
  // Configuration du widget
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig | null>(null);
  const [widgetLayout, setWidgetLayout] = useState<WidgetLayout>({
    x: 0,
    y: 0,
    w: 4,
    h: 3,
  });
  
  const steps = [
    { key: "selection", label: "Sélection du type" },
    { key: "configuration", label: "Configuration" },
  ];
  
  // Track if initial load has happened for this modal session
  const hasInitializedRef = useRef(false);
  const previousOpenRef = useRef(false);
  
  // Initialize state when modal opens (ONLY on open transition, not on editingWidget changes)
  useEffect(() => {
    const wasJustOpened = open && !previousOpenRef.current;
    previousOpenRef.current = open;
    
    // Only initialize on modal OPEN transition
    if (wasJustOpened) {
      if (editingWidget) {
        // Edit mode
        setWidgetConfig(editingWidget.config);
        setWidgetLayout(editingWidget.layout);
        setActiveStep(1);
      } else {
        // Create mode
        setWidgetConfig(null);
        setWidgetLayout({ x: 0, y: 0, w: 4, h: 3 });
        setActiveStep(0);
      }
      hasInitializedRef.current = true;
    } else if (!open) {
      // Reset the flag when modal closes
      hasInitializedRef.current = false;
    }
  }, [open, editingWidget]);
  
  // Sélection d'un type de widget
  const handleWidgetSelection = useCallback((type: WidgetType) => {
    const meta = WIDGET_REGISTRY[type];
    const newConfig = createDefaultWidgetConfig(type, `widget-${Date.now()}`);
    
    setWidgetConfig(newConfig);
    setWidgetLayout({
      x: 0,
      y: 0,
      w: meta?.defaultSize.w ?? 4,
      h: meta?.defaultSize.h ?? 3,
    });
    setActiveStep(1);
  }, []);
  
  // Navigation
  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };
  
  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };
  
  // Sauvegarde
  const handleSave = () => {
    if (!widgetConfig) return;
    
    const widget: Widget = {
      id: widgetConfig.id,
      config: widgetConfig,
      layout: widgetLayout,
    };
    
    if (isEditMode && onUpdateWidget) {
      onUpdateWidget(widget);
    } else {
      onAddWidget(widget);
    }
    
    onClose();
  };
  
  // Validation
  const canProceed = activeStep === 0 && widgetConfig !== null;
  const canFinish = activeStep === 1 && widgetConfig !== null;
  
  // Contenu de l'étape
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        if (isEditMode) return null;
        return <WidgetSelectionStep onSelectWidget={handleWidgetSelection} />;
        
      case 1:
        if (!widgetConfig) {
          return (
            <div className="h-[550px] flex items-center justify-center text-content-tertiary">
              <Typography variant="text-sm">
                Sélectionnez d'abord un type de widget
              </Typography>
            </div>
          );
        }
        return (
          <WidgetConfigurationStep
            config={widgetConfig}
            onConfigChange={setWidgetConfig}
            filtersCtx={filtersCtx}
          />
        );
        
      default:
        return null;
    }
  };
  
  // Footer avec indicateurs de step (pattern alert-form-modal)
  const renderFooter = () => (
    <div className="flex items-center justify-between w-full">
      {/* Step indicators */}
      <div className="flex items-center gap-2">
        {!isEditMode && steps.map((step, index) => (
          <div
            key={step.key}
            className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
              ${
                index === activeStep
                  ? "bg-accent text-white"
                  : index < activeStep
                    ? "bg-emerald-500 text-white"
                    : "bg-surface-secondary text-content-muted"
              }
            `}
          >
            {index < activeStep ? <FaCheck className="text-xs" /> : index + 1}
          </div>
        ))}
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-2">
        {activeStep > 0 && !isEditMode && (
          <Button
            variant="ghost"
            onClick={handlePrevious}
            leftIcon={<FaArrowLeft />}
          >
            Retour
          </Button>
        )}
        {canProceed && (
          <Button
            variant="primary"
            onClick={handleNext}
            rightIcon={<FaArrowRight />}
          >
            Suivant
          </Button>
        )}
        {canFinish && (
          <Button
            variant="primary"
            onClick={handleSave}
            leftIcon={<FaSave />}
          >
            {isEditMode ? "Sauvegarder" : "Ajouter"}
          </Button>
        )}
      </div>
    </div>
  );
  
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      icon={<FaLayerGroup />}
      title={isEditMode ? "Modifier le widget" : "Ajouter un widget"}
      subtitle={isEditMode ? steps[1].label : steps[activeStep]?.label}
      variant="accent"
      size="full"
      className="!max-w-[1200px]"
      customFooter={renderFooter()}
    >
      <div className="min-h-[400px]">{renderStepContent()}</div>
    </BaseModal>
  );
};

export default WidgetStepperModalV2;
